/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
  AfterContentInit,
  Component,
  ContentChildren,
  ElementRef,
  Input,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
  ChangeDetectorRef,
} from '@angular/core';
import {MdOption} from '../core';
import {ActiveDescendantKeyManager} from '../core/a11y/activedescendant-key-manager';
/**
 * Autocomplete IDs need to be unique across components, so this counter exists outside of
 * the component definition.
 */
let _uniqueAutocompleteIdCounter = 0;

export type AutocompletePositionY = 'above' | 'below';
export class MdAutocomplete implements AfterContentInit {
/**
 * Manages active item in option list based on key events.
 */
_keyManager: ActiveDescendantKeyManager;
/**
 * Whether the autocomplete panel displays above or below its trigger.
 */
positionY: AutocompletePositionY = 'below';
/**
 * Whether the autocomplete panel should be visible, depending on option length.
 */
showPanel = false;
/**
 * \@docs-private
 */
template: TemplateRef<any>;
/**
 * Element for the panel containing the autocomplete options.
 */
panel: ElementRef;
/**
 * \@docs-private
 */
options: QueryList<MdOption>;
/**
 * Function that maps an option's control value to its display value in the trigger.
 */
displayWith: (value: any) => string;
/**
 * Unique ID to be used by autocomplete trigger's "aria-owns" property.
 */
id: string = `md-autocomplete-${_uniqueAutocompleteIdCounter++}`;
/**
 * @param {?} _changeDetectorRef
 */
constructor(private _changeDetectorRef: ChangeDetectorRef) { }
/**
 * @return {?}
 */
ngAfterContentInit() {
    this._keyManager = new ActiveDescendantKeyManager(this.options).withWrap();
  }
/**
 * Sets the panel scrollTop. This allows us to manually scroll to display
 * options below the fold, as they are not actually being focused when active.
 * @param {?} scrollTop
 * @return {?}
 */
_setScrollTop(scrollTop: number): void {
    if (this.panel) {
      this.panel.nativeElement.scrollTop = scrollTop;
    }
  }
/**
 * Panel should hide itself when the option list is empty.
 * @return {?}
 */
_setVisibility() {
    Promise.resolve().then(() => {
      this.showPanel = !!this.options.length;
      this._changeDetectorRef.markForCheck();
    });
  }
/**
 * Sets a class on the panel based on its position (used to set y-offset).
 * @return {?}
 */
_getClassList() {
    return {
      'mat-autocomplete-panel-below': this.positionY === 'below',
      'mat-autocomplete-panel-above': this.positionY === 'above',
      'mat-autocomplete-visible': this.showPanel,
      'mat-autocomplete-hidden': !this.showPanel
    };
  }

static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: 'md-autocomplete, mat-autocomplete',
  templateUrl: 'autocomplete.html',
  styleUrls: ['autocomplete.css'],
  encapsulation: ViewEncapsulation.None,
  exportAs: 'mdAutocomplete',
  host: {
    'class': 'mat-autocomplete'
  }
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ChangeDetectorRef, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'template': [{ type: ViewChild, args: [TemplateRef, ] },],
'panel': [{ type: ViewChild, args: ['panel', ] },],
'options': [{ type: ContentChildren, args: [MdOption, ] },],
'displayWith': [{ type: Input },],
};
}

function MdAutocomplete_tsickle_Closure_declarations() {
/** @type {?} */
MdAutocomplete.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdAutocomplete.ctorParameters;
/** @type {?} */
MdAutocomplete.propDecorators;
/**
 * Manages active item in option list based on key events.
 * @type {?}
 */
MdAutocomplete.prototype._keyManager;
/**
 * Whether the autocomplete panel displays above or below its trigger.
 * @type {?}
 */
MdAutocomplete.prototype.positionY;
/**
 * Whether the autocomplete panel should be visible, depending on option length.
 * @type {?}
 */
MdAutocomplete.prototype.showPanel;
/**
 * \@docs-private
 * @type {?}
 */
MdAutocomplete.prototype.template;
/**
 * Element for the panel containing the autocomplete options.
 * @type {?}
 */
MdAutocomplete.prototype.panel;
/**
 * \@docs-private
 * @type {?}
 */
MdAutocomplete.prototype.options;
/**
 * Function that maps an option's control value to its display value in the trigger.
 * @type {?}
 */
MdAutocomplete.prototype.displayWith;
/**
 * Unique ID to be used by autocomplete trigger's "aria-owns" property.
 * @type {?}
 */
MdAutocomplete.prototype.id;
/** @type {?} */
MdAutocomplete.prototype._changeDetectorRef;
}



interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
